import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DAY_SLOTS,
  NO_DEVIATION,
  addDays,
  deviationId,
  eatersOf,
  householdMealWriteRule,
  isWorkoutMeal,
  mealRules,
  mealRulesId,
  namedDish,
  readDeviation,
  totalPortions,
} from '@kraftwerk/shared';
import type { DaySlot, EaterLine, MealDeviation, MealRules } from '@kraftwerk/shared';
import { currentOwnerId } from '../../auth.js';
import { db, ignoreClosed } from '../../db/local.js';
import type {
  LocalHouseholdMeal,
  LocalHouseholdMealEater,
  LocalHouseholdMember,
  LocalMealDeviation,
  LocalRecipe,
} from '../../db/local.js';
import {
  activeMembers,
  addWish,
  dishFromRecipe,
  listEaters,
  listHouseholdMeals,
  listWishes,
  mayWriteEater,
  planHouseholdMeal,
  removeHouseholdMeal,
  saveDeviation,
  setChildPortions,
  syncHouseholdMeals,
  takeWish,
} from '../../db/householdMeals.js';
import type { MealContext } from '../../db/householdMeals.js';
import { listRecipes } from '../../db/nutrition.js';
import { currentHouseholdId } from '../../db/shopping.js';
import { formatDay } from '../../format.js';
import { useToday } from '../../hooks/useToday.js';
import { useChildLevel, useIsChild, useModules } from '../../hooks/useModules.js';

/** Plätze, die der Haushalt plant — Pre/Post liegen ums eigene Training. */
const HOUSEHOLD_SLOTS = DAY_SLOTS.filter((slot) => !isWorkoutMeal(slot));
/** Portionen, die Erwachsene für ein Kind wählen können. */
const CHILD_PORTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

/** Wer ich bin, ob meine Bilanz zählt und welcher Tag heute ist. */
export function useMealContext(): MealContext | null {
  const me = currentOwnerId();
  const nutritionOn = useModules().includes('nutrition');
  const today = useToday();
  if (!me || !today) return null;
  return { me, nutritionOn, today };
}

/**
 * Raum Haushalt → Essen (27.10.2026): der Plan der Woche, wer mitisst,
 * Wünsche. Jedes Mitglied isst ab Werk mit einer Portion mit; abweichen
 * geht hier (mitessen ja/nein, Portion der Kinder) oder ausführlich im
 * eigenen Tag unter Ernährung. Den Plan schreiben Erwachsene, Kinder ab
 * Stufe `school` tragen Wünsche ein — dieselbe Regel wie beim Server.
 */
export function HouseholdMeals({ onOpenList }: { onOpenList: () => void }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const context = useMealContext();
  const child = useIsChild();
  const level = useChildLevel();
  const householdId = useLiveQuery(() => currentHouseholdId(db), [], undefined);
  const [offset, setOffset] = useState(0);
  const start = context ? addDays(context.today, offset * 7) : null;
  const end = start ? addDays(start, 6) : null;

  const members = useLiveQuery(
    () => activeMembers(householdId ?? null, db),
    [householdId],
    [] as LocalHouseholdMember[],
  );
  const meals = useLiveQuery(
    () =>
      start && end ? listHouseholdMeals(householdId ?? null, start, end, db) : Promise.resolve([]),
    [householdId, start, end],
    [] as LocalHouseholdMeal[],
  );
  const eaters = useLiveQuery(
    () => listEaters(householdId ?? null, db),
    [householdId],
    [] as LocalHouseholdMealEater[],
  );
  const deviations = useLiveQuery(
    () => db.meal_deviations.toArray(),
    [],
    [] as LocalMealDeviation[],
  );
  const recipes = useLiveQuery(
    () => (child ? Promise.resolve([]) : listRecipes(db)),
    [child],
    [] as LocalRecipe[],
  );

  // Mitess-Zeilen und Protokoll für die kommenden Tage nachziehen.
  const mealKey = meals.map((meal) => `${meal.id}:${meal.updatedAt}`).join();
  useEffect(() => {
    if (!context || householdId === undefined) return;
    syncHouseholdMeals(householdId, context, db).catch(ignoreClosed);
    // `context` ist je Darstellung neu — es zählen seine Werte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [householdId, mealKey, context?.me, context?.today, context?.nutritionOn]);

  if (householdId === null) {
    return (
      <p role="status" className="kw-tile text-sm text-warn">
        {t('householdMeals.noHousehold')}
      </p>
    );
  }
  if (!context || !start || householdId === undefined) {
    return <p className="text-sm text-ink-muted">{t('householdMeals.loading')}</p>;
  }

  const me = members.find((member) => member.userId === context.me);
  const actor = {
    role: child ? ('child' as const) : ('adult' as const),
    level: child ? level : null,
  };
  const mayPlan = householdMealWriteRule(actor, context.me, { status: 'planned' }, null) === null;
  const mayWish = householdMealWriteRule(actor, context.me, { status: 'wish' }, null) === null;
  const rules = mealRules(
    deviations.find((row) => row.id === mealRulesId(context.me) && !row.deletedAt)?.rules,
  );
  const deviationOf = (entryId: string): MealDeviation | null => {
    const row = deviations.find((entry) => entry.id === deviationId(context.me, entryId));
    return row && !row.deletedAt ? readDeviation(row) : null;
  };
  const days = Array.from({ length: 7 }, (_, index) => addDays(start, index));

  return (
    <section aria-labelledby="household-meals-heading" className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 id="household-meals-heading" className="text-lg font-semibold">
          {t('householdMeals.heading')}
        </h2>
        <p className="text-sm text-ink-muted">{t('householdMeals.intro')}</p>
      </div>

      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setOffset((value) => value - 1)}
          className="kw-btn kw-btn-quiet kw-btn-small px-3"
          aria-label={t('householdMeals.prevWeek')}
        >
          ‹
        </button>
        <p className="text-center text-sm font-medium">
          {t('householdMeals.weekOf', { date: formatDay(start, 'short', i18n.language) })}
        </p>
        <span className="flex gap-1">
          {offset !== 0 ? (
            <button
              type="button"
              onClick={() => setOffset(0)}
              className="kw-btn kw-btn-quiet kw-btn-small px-3"
            >
              {t('householdMeals.thisWeek')}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setOffset((value) => value + 1)}
            className="kw-btn kw-btn-quiet kw-btn-small px-3"
            aria-label={t('householdMeals.nextWeek')}
          >
            ›
          </button>
        </span>
      </div>

      <ol className="flex flex-col gap-3">
        {days.map((date) => (
          <li key={date}>
            <MealDay
              date={date}
              householdId={householdId}
              meals={meals.filter((meal) => meal.date === date)}
              members={members}
              eaters={eaters}
              me={me}
              context={context}
              rules={rules}
              deviationOf={deviationOf}
              mayPlan={mayPlan}
              recipes={recipes}
            />
          </li>
        ))}
      </ol>

      <Wishes
        householdId={householdId}
        members={members}
        context={context}
        mayPlan={mayPlan}
        mayWish={mayWish}
        recipes={recipes}
        days={days}
      />

      <div className="kw-tile flex flex-col gap-2">
        <p className="text-sm text-ink-muted">{t('householdMeals.toShoppingHint')}</p>
        <button type="button" onClick={onOpenList} className="kw-btn kw-btn-quiet">
          {t('householdMeals.toShopping')}
        </button>
      </div>
    </section>
  );
}

function slotIndex(slot: DaySlot | null): number {
  return slot ? DAY_SLOTS.indexOf(slot) : DAY_SLOTS.length;
}

function MealDay({
  date,
  householdId,
  meals,
  members,
  eaters,
  me,
  context,
  rules,
  deviationOf,
  mayPlan,
  recipes,
}: {
  date: string;
  householdId: string;
  meals: LocalHouseholdMeal[];
  members: LocalHouseholdMember[];
  eaters: LocalHouseholdMealEater[];
  me: LocalHouseholdMember | undefined;
  context: MealContext;
  rules: MealRules;
  deviationOf: (entryId: string) => MealDeviation | null;
  mayPlan: boolean;
  recipes: LocalRecipe[];
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [adding, setAdding] = useState(false);
  const day = formatDay(date, 'long', i18n.language);
  const sorted = [...meals].sort((a, b) => slotIndex(a.slot) - slotIndex(b.slot));

  return (
    <div className="kw-tile">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-medium">{day}</h3>
        {mayPlan ? (
          <button
            type="button"
            onClick={() => setAdding((value) => !value)}
            aria-expanded={adding}
            aria-label={t('householdMeals.addNamed', { day })}
            className="kw-btn kw-btn-quiet kw-btn-small px-3"
          >
            {t('householdMeals.add')}
          </button>
        ) : null}
      </div>
      {sorted.length === 0 ? (
        <p className="mt-2 text-sm text-ink-muted">{t('householdMeals.dayEmpty')}</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-2">
          {sorted.map((meal) => (
            <MealCard
              key={meal.id}
              meal={meal}
              members={members}
              eaters={eaters}
              me={me}
              context={context}
              rules={rules}
              deviation={deviationOf(meal.id)}
              mayPlan={mayPlan}
            />
          ))}
        </ul>
      )}
      {adding ? (
        <AddMeal
          date={date}
          householdId={householdId}
          members={members}
          recipes={recipes}
          onDone={() => setAdding(false)}
        />
      ) : null}
    </div>
  );
}

/** „1,5 Portionen“, „isst nicht mit“, „eigene Variante: …“. */
function ShareText({ line }: { line: EaterLine }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const parts: string[] = [];
  if (line.share.variant) parts.push(t('householdMeals.variant', { name: line.share.variant }));
  else if (line.share.portions === 0) parts.push(t('householdMeals.notEating'));
  else
    parts.push(
      t('householdMeals.portions', {
        count: line.share.portions,
        amount: line.share.portions.toLocaleString(i18n.language),
      }),
    );
  if (line.share.items.length > 0 && !line.share.variant) {
    parts.push(t('householdMeals.extras', { count: line.share.items.length }));
  }
  if (line.share.vegetarian) parts.push(t('householdMeals.vegetarian'));
  return <>{parts.join(' · ')}</>;
}

function MealCard({
  meal,
  members,
  eaters,
  me,
  context,
  rules,
  deviation,
  mayPlan,
}: {
  meal: LocalHouseholdMeal;
  members: LocalHouseholdMember[];
  eaters: LocalHouseholdMealEater[];
  me: LocalHouseholdMember | undefined;
  context: MealContext;
  rules: MealRules;
  deviation: MealDeviation | null;
  mayPlan: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const lines = eatersOf(
    meal.id,
    members.map((member) => member.userId),
    eaters,
  );
  const nameOf = (userId: string) =>
    members.find((member) => member.userId === userId)?.displayName ||
    t('householdMeals.unknownMember');
  const cook = meal.cookId ? nameOf(meal.cookId) : null;
  const per = meal.dish.perPortion;
  const mine = deviation ?? NO_DEVIATION;
  const total = totalPortions(lines);

  return (
    <li className="flex flex-col gap-2 rounded-xl border border-line px-3 py-2 text-sm">
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0">
          <span className="kw-kicker block">
            {meal.slot ? t(`nutrition.slot.${meal.slot}`) : ''}
          </span>
          <span className="block font-semibold break-words">{meal.dish.name}</span>
          <span className="block text-xs text-ink-muted">
            {per
              ? t('householdMeals.perPortion', {
                  kcal: Math.round(per.kcal),
                  protein: Math.round(per.protein),
                })
              : t('householdMeals.noValues')}
            {meal.dish.sourceNote
              ? ` · ${t('householdMeals.source', { source: meal.dish.sourceNote })}`
              : ''}
          </span>
          {cook ? (
            <span className="block text-xs text-ink-muted">
              {t('householdMeals.cooks', { name: cook })}
            </span>
          ) : null}
          {meal.note ? <span className="block text-xs break-words">{meal.note}</span> : null}
        </span>
        {mayPlan ? (
          <button
            type="button"
            onClick={() => void removeHouseholdMeal(meal.id, db)}
            aria-label={t('householdMeals.removeNamed', { name: meal.dish.name })}
            className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3 hover:border-danger hover:text-danger"
          >
            ×
          </button>
        ) : null}
      </div>

      <div>
        <h4 className="text-xs font-medium text-ink-muted">
          {t('householdMeals.eaters', {
            amount: total.toLocaleString(i18n.language),
          })}
        </h4>
        <ul className="mt-1 flex flex-col gap-1">
          {lines.map((line) => {
            const member = members.find((entry) => entry.userId === line.userId);
            const isMe = line.userId === context.me;
            const shared = line.share.portions > 0 || line.share.variant !== null;
            // Ohne eigene Abweichung gilt die Zeile (ein Kind: von den Eltern gesetzt).
            const eats = isMe && deviation ? !mine.skip : shared;
            const mayChange = mayWriteEater(me, member);
            const name = nameOf(line.userId);
            return (
              <li key={line.userId} className="flex flex-wrap items-center justify-between gap-2">
                <span className="min-w-0">
                  <span className="font-medium">{isMe ? t('householdMeals.me') : name}</span>
                  <span className="text-ink-muted">
                    {' · '}
                    <ShareText line={line} />
                  </span>
                </span>
                {mayChange ? (
                  <span className="flex shrink-0 items-center gap-1">
                    {!isMe && member?.role === 'child' && eats ? (
                      <ChildPortion meal={meal} childId={line.userId} name={name} line={line} />
                    ) : null}
                    <button
                      type="button"
                      className="kw-toggle whitespace-nowrap"
                      aria-pressed={eats}
                      aria-label={
                        isMe
                          ? t('householdMeals.meEatsNamed', { name: meal.dish.name })
                          : t('householdMeals.eatsNamed', { name, dish: meal.dish.name })
                      }
                      onClick={() => {
                        if (isMe) {
                          void saveDeviation(meal, { ...mine, skip: !mine.skip }, context, db);
                        } else {
                          void setChildPortions(meal, line.userId, eats ? 0 : 1, db);
                        }
                      }}
                    >
                      {eats ? t('householdMeals.eats') : t('householdMeals.notEatingShort')}
                    </button>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
        {rules.vegetarian && !mine.variant && !mine.skip ? (
          <p className="mt-1 text-xs text-ink-muted">{t('householdMeals.vegetarianSelf')}</p>
        ) : null}
      </div>
    </li>
  );
}

function ChildPortion({
  meal,
  childId,
  name,
  line,
}: {
  meal: LocalHouseholdMeal;
  childId: string;
  name: string;
  line: EaterLine;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const id = useId();
  const value = CHILD_PORTIONS.includes(line.share.portions as never) ? line.share.portions : 1;
  return (
    <>
      <label htmlFor={id} className="sr-only">
        {t('householdMeals.portionFor', { name })}
      </label>
      <select
        id={id}
        value={String(value)}
        onChange={(event) => void setChildPortions(meal, childId, Number(event.target.value), db)}
        className="kw-field text-ink"
      >
        {CHILD_PORTIONS.map((amount) => (
          <option key={amount} value={String(amount)}>
            {amount.toLocaleString(i18n.language)}×
          </option>
        ))}
      </select>
    </>
  );
}

function AddMeal({
  date,
  householdId,
  members,
  recipes,
  onDone,
}: {
  date: string;
  householdId: string;
  members: LocalHouseholdMember[];
  recipes: LocalRecipe[];
  onDone: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const ids = { slot: useId(), recipe: useId(), name: useId(), note: useId(), cook: useId() };
  const [kind, setKind] = useState<'recipe' | 'free'>(recipes.length > 0 ? 'recipe' : 'free');
  const [slot, setSlot] = useState<DaySlot>('dinner');
  const [recipe, setRecipe] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [cook, setCook] = useState('');

  return (
    <form
      className="mt-3 flex flex-col gap-2 rounded-lg border border-accent bg-surface-raised p-3"
      onSubmit={(event) => {
        event.preventDefault();
        const chosen = recipes.find((entry) => entry.id === recipe);
        if (kind === 'recipe' && !chosen) return;
        if (kind === 'free' && !name.trim()) return;
        void (async () => {
          const dish =
            chosen && kind === 'recipe' ? await dishFromRecipe(chosen, db) : namedDish(name);
          await planHouseholdMeal(
            { householdId, date, slot, dish, note, cookId: cook || null },
            db,
          );
          onDone();
        })();
      }}
    >
      <div className="flex w-full gap-1">
        {(['recipe', 'free'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={kind === option}
            onClick={() => setKind(option)}
            className={`kw-btn kw-btn-small flex-1 ${kind === option ? '' : 'kw-btn-quiet text-ink'}`}
          >
            {t(`householdMeals.form.${option}`)}
          </button>
        ))}
      </div>
      <label htmlFor={ids.slot} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('householdMeals.form.slot')}
        <select
          id={ids.slot}
          value={slot}
          onChange={(event) => setSlot(event.target.value as DaySlot)}
          className="kw-field text-ink"
        >
          {HOUSEHOLD_SLOTS.map((entry) => (
            <option key={entry} value={entry}>
              {t(`nutrition.slot.${entry}`)}
            </option>
          ))}
        </select>
      </label>
      {kind === 'recipe' ? (
        <label htmlFor={ids.recipe} className="flex flex-col gap-1 text-xs text-ink-muted">
          {t('householdMeals.form.pickRecipe')}
          <select
            id={ids.recipe}
            value={recipe}
            onChange={(event) => setRecipe(event.target.value)}
            className="kw-field text-ink"
          >
            <option value="">{t('householdMeals.form.choose')}</option>
            {recipes.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <label htmlFor={ids.name} className="flex flex-col gap-1 text-xs text-ink-muted">
          {t('householdMeals.form.name')}
          <input
            id={ids.name}
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
            className="kw-field text-ink"
          />
        </label>
      )}
      <label htmlFor={ids.note} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('householdMeals.form.note')}
        <input
          id={ids.note}
          value={note}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
          className="kw-field text-ink"
        />
      </label>
      <label htmlFor={ids.cook} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('householdMeals.form.cook')}
        <select
          id={ids.cook}
          value={cook}
          onChange={(event) => setCook(event.target.value)}
          className="kw-field text-ink"
        >
          <option value="">{t('householdMeals.form.nobody')}</option>
          {members.map((member) => (
            <option key={member.userId} value={member.userId}>
              {member.displayName}
            </option>
          ))}
        </select>
      </label>
      <p className="text-xs text-ink-muted">{t('householdMeals.form.frozenHint')}</p>
      <div className="flex gap-2">
        <button type="submit" className="kw-btn kw-btn-small flex-1">
          {t('householdMeals.form.save')}
        </button>
        <button type="button" onClick={onDone} className="kw-btn kw-btn-quiet kw-btn-small px-4">
          {t('householdMeals.form.cancel')}
        </button>
      </div>
    </form>
  );
}

function Wishes({
  householdId,
  members,
  context,
  mayPlan,
  mayWish,
  recipes,
  days,
}: {
  householdId: string;
  members: LocalHouseholdMember[];
  context: MealContext;
  mayPlan: boolean;
  mayWish: boolean;
  recipes: LocalRecipe[];
  days: string[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const wishes = useLiveQuery(
    () => listWishes(householdId, db),
    [householdId],
    [] as LocalHouseholdMeal[],
  );
  const ids = { name: useId(), note: useId() };
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [taking, setTaking] = useState<string | null>(null);
  const nameOf = (userId: string | undefined) =>
    members.find((member) => member.userId === userId)?.displayName ||
    t('householdMeals.unknownMember');

  return (
    <section aria-labelledby="household-wishes" className="kw-tile flex flex-col gap-2">
      <h3 id="household-wishes" className="kw-kicker">
        {t('householdMeals.wishes.heading')}
      </h3>
      <p className="text-sm text-ink-muted">{t('householdMeals.wishes.intro')}</p>
      {wishes.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('householdMeals.wishes.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {wishes.map((wish) => {
            const own = wish.userId === undefined || wish.userId === context.me;
            return (
              <li
                key={wish.id}
                className="flex flex-col gap-2 rounded-xl border border-line px-3 py-2 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="min-w-0">
                    <span className="block font-semibold break-words">{wish.dish.name}</span>
                    <span className="block text-xs text-ink-muted">
                      {t('householdMeals.wishes.from', {
                        name: own ? t('householdMeals.me') : nameOf(wish.userId),
                      })}
                      {wish.note ? ` · ${wish.note}` : ''}
                    </span>
                  </span>
                  <span className="flex shrink-0 gap-1">
                    {mayPlan ? (
                      <button
                        type="button"
                        onClick={() => setTaking((value) => (value === wish.id ? null : wish.id))}
                        aria-expanded={taking === wish.id}
                        aria-label={t('householdMeals.wishes.takeNamed', { name: wish.dish.name })}
                        className="kw-btn kw-btn-small px-3"
                      >
                        {t('householdMeals.wishes.take')}
                      </button>
                    ) : null}
                    {mayPlan || (mayWish && own) ? (
                      <button
                        type="button"
                        onClick={() => void removeHouseholdMeal(wish.id, db)}
                        aria-label={
                          own
                            ? t('householdMeals.wishes.removeOwnNamed', { name: wish.dish.name })
                            : t('householdMeals.wishes.declineNamed', { name: wish.dish.name })
                        }
                        className="kw-btn kw-btn-quiet kw-btn-small px-3"
                      >
                        {own
                          ? t('householdMeals.wishes.removeOwn')
                          : t('householdMeals.wishes.decline')}
                      </button>
                    ) : null}
                  </span>
                </div>
                {taking === wish.id ? (
                  <TakeWish
                    wish={wish}
                    recipes={recipes}
                    days={days}
                    onDone={() => setTaking(null)}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {mayWish ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!name.trim()) return;
            void addWish({ householdId, name, note }, db).then(() => {
              setName('');
              setNote('');
            });
          }}
        >
          <label htmlFor={ids.name} className="flex flex-col gap-1 text-xs text-ink-muted">
            {t('householdMeals.wishes.name')}
            <input
              id={ids.name}
              value={name}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
              className="kw-field text-ink"
            />
          </label>
          <label htmlFor={ids.note} className="flex flex-col gap-1 text-xs text-ink-muted">
            {t('householdMeals.wishes.note')}
            <input
              id={ids.note}
              value={note}
              maxLength={500}
              onChange={(event) => setNote(event.target.value)}
              className="kw-field text-ink"
            />
          </label>
          <button type="submit" className="kw-btn kw-btn-quiet kw-btn-small">
            {t('householdMeals.wishes.save')}
          </button>
        </form>
      ) : (
        <p className="text-xs text-ink-muted">{t('householdMeals.wishes.smallChild')}</p>
      )}
    </section>
  );
}

function TakeWish({
  wish,
  recipes,
  days,
  onDone,
}: {
  wish: LocalHouseholdMeal;
  recipes: LocalRecipe[];
  days: string[];
  onDone: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const ids = { day: useId(), slot: useId(), recipe: useId() };
  const [date, setDate] = useState(wish.date ?? days[0]!);
  const [slot, setSlot] = useState<DaySlot>(wish.slot ?? 'dinner');
  const match = recipes.find(
    (recipe) =>
      recipe.name.trim().toLocaleLowerCase('de') === wish.dish.name.toLocaleLowerCase('de'),
  );
  const [recipe, setRecipe] = useState(match?.id ?? '');

  return (
    <form
      className="flex flex-col gap-2 rounded-lg border border-accent bg-surface-raised p-3"
      onSubmit={(event) => {
        event.preventDefault();
        void (async () => {
          const chosen = recipes.find((entry) => entry.id === recipe);
          const dish = chosen ? await dishFromRecipe(chosen, db) : null;
          await takeWish(wish.id, { date, slot, dish }, db);
          onDone();
        })();
      }}
    >
      <label htmlFor={ids.day} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('householdMeals.wishes.day')}
        <select
          id={ids.day}
          value={date}
          onChange={(event) => setDate(event.target.value)}
          className="kw-field text-ink"
        >
          {[...new Set([date, ...days])].sort().map((day) => (
            <option key={day} value={day}>
              {formatDay(day, 'long', i18n.language)}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor={ids.slot} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('householdMeals.form.slot')}
        <select
          id={ids.slot}
          value={slot}
          onChange={(event) => setSlot(event.target.value as DaySlot)}
          className="kw-field text-ink"
        >
          {HOUSEHOLD_SLOTS.map((entry) => (
            <option key={entry} value={entry}>
              {t(`nutrition.slot.${entry}`)}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor={ids.recipe} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('householdMeals.wishes.takeWith')}
        <select
          id={ids.recipe}
          value={recipe}
          onChange={(event) => setRecipe(event.target.value)}
          className="kw-field text-ink"
        >
          <option value="">{t('householdMeals.wishes.takeNoRecipe')}</option>
          {recipes.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name}
            </option>
          ))}
        </select>
      </label>
      <button type="submit" className="kw-btn kw-btn-small">
        {t('householdMeals.wishes.take')}
      </button>
    </form>
  );
}
